import { Button, Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui';
import { Loader2 } from 'lucide-react';
import type {
  AccountingInvoicePostingMode,
  AccountingPaymentSyncMode,
  AccountingPostingCatalogAccount,
  AccountingPostingCatalogTaxRate,
} from '../../../../../services/accountingIntegration';
import {
  buildAccountSelectOptions,
  buildTaxSelectOptions,
  selectClassName,
} from './accountingIntegrationUi';
import { AccountingWorkflowSettings } from './AccountingWorkflowSettings';

interface Props {
  catalogAccounts: AccountingPostingCatalogAccount[] | null;
  catalogTaxRates: AccountingPostingCatalogTaxRate[] | null;
  catalogLoading: boolean;
  paymentAccountId: string;
  salesAccountId: string;
  taxCodeId: string;
  invoicePostingMode: AccountingInvoicePostingMode;
  paymentSyncMode: AccountingPaymentSyncMode;
  saveBusy: boolean;
  onPaymentAccountIdChange: (value: string) => void;
  onSalesAccountIdChange: (value: string) => void;
  onTaxCodeIdChange: (value: string) => void;
  onInvoicePostingModeChange: (mode: AccountingInvoicePostingMode) => void;
  onPaymentSyncModeChange: (mode: AccountingPaymentSyncMode) => void;
  onSave: () => void;
}

export function AccountingPostingDefaults({
  catalogAccounts,
  catalogTaxRates,
  catalogLoading,
  paymentAccountId,
  salesAccountId,
  taxCodeId,
  invoicePostingMode,
  paymentSyncMode,
  saveBusy,
  onPaymentAccountIdChange,
  onSalesAccountIdChange,
  onTaxCodeIdChange,
  onInvoicePostingModeChange,
  onPaymentSyncModeChange,
  onSave,
}: Props) {
  const paymentOptions = buildAccountSelectOptions(catalogAccounts, paymentAccountId);
  const salesOptions = buildAccountSelectOptions(catalogAccounts, salesAccountId);
  const taxOptions = buildTaxSelectOptions(catalogTaxRates, taxCodeId);

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4 space-y-5">
      <AccountingWorkflowSettings
        invoicePostingMode={invoicePostingMode}
        paymentSyncMode={paymentSyncMode}
        disabled={saveBusy}
        onInvoicePostingModeChange={onInvoicePostingModeChange}
        onPaymentSyncModeChange={onPaymentSyncModeChange}
      />

      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Account mapping</h4>
        <FieldDescription className="mb-3">
          Optional. Choose which accounts in your accounting software CourtSync posts transactions to.
          If left blank, CourtSync will pick the most appropriate accounts automatically.
        </FieldDescription>

        {catalogLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Loading your accounts…
          </div>
        ) : (
          <FieldGroup className="gap-3">
            {!catalogAccounts && !catalogLoading ? (
              <p className="text-xs text-amber-800 dark:text-amber-200/90">
                Your account list could not be loaded right now. You can still save to reset to
                automatic selection, or leave as-is to keep your current settings.
              </p>
            ) : null}
            <Field>
              <FieldLabel htmlFor="posting-payment-account">Bank account</FieldLabel>
              <select
                id="posting-payment-account"
                className={selectClassName}
                value={paymentAccountId}
                onChange={(event) => onPaymentAccountIdChange(event.target.value)}
                disabled={saveBusy}
              >
                {paymentOptions.map((option) => (
                  <option key={`payment-${option.value || 'auto'}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field>
              <FieldLabel htmlFor="posting-sales-account">Income account</FieldLabel>
              <select
                id="posting-sales-account"
                className={selectClassName}
                value={salesAccountId}
                onChange={(event) => onSalesAccountIdChange(event.target.value)}
                disabled={saveBusy}
              >
                {salesOptions.map((option) => (
                  <option key={`sales-${option.value || 'auto'}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field>
              <FieldLabel htmlFor="posting-tax-code">Tax code</FieldLabel>
              <select
                id="posting-tax-code"
                className={selectClassName}
                value={taxCodeId}
                onChange={(event) => onTaxCodeIdChange(event.target.value)}
                disabled={saveBusy}
              >
                {taxOptions.map((option) => (
                  <option key={`tax-${option.value || 'auto'}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
            <Button type="button" onClick={onSave} disabled={saveBusy}>
              {saveBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2 inline" aria-hidden /> : null}
              Save settings
            </Button>
          </FieldGroup>
        )}
      </div>
    </div>
  );
}
